import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Dropdown, TextField } from '@soluto-private/mx-asurion-ui-react';

import { STATE_DROPDOWN_OPTIONS } from '@service-initiation/shared-data-access-us-states';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { useBillingDetailsAtom } from '@service-initiation/tech-care-data-access-store';

import { billingDetailsFormSchema } from '../schemas';
import { PaymentForm, PaymentRow } from './SharedStyledComponents';

const BillingAddressFormContainer = styled.div`
  margin-bottom: 32px;
`;

const TEXT = TranslationEntries.Repairs.BillingDetails;

export const BillingAddressForm = () => {
  const [billingDetails, setBillingDetails] = useBillingDetailsAtom();
  const { t } = useTranslation();
  const [errorFields, setErrorFields] = useState({
    city: '',
    line1: '',
    line2: '',
    postalCode: '',
    state: '',
  });

  const validateForm = ({
    fieldName,
    fieldValue,
  }: {
    fieldName: string;
    fieldValue: unknown;
  }) => {
    const fieldData = {
      billingIsSame: billingDetails?.billingIsSame,
      [fieldName]: fieldValue,
    };

    billingDetailsFormSchema(t)
      .validateAt(fieldName, fieldData)
      .then(() => {
        setErrorFields({ ...errorFields, ...{ [fieldName]: '' } });
      })
      .catch((error) => {
        setErrorFields({
          ...errorFields,
          ...{ [fieldName]: error?.errors[0] },
        });
      });
  };

  const handleBlur = ({
    target: { name, value },
  }: React.BaseSyntheticEvent<Partial<HTMLInputElement>>) => {
    validateForm({ fieldName: name, fieldValue: value });
  };

  return (
    <BillingAddressFormContainer>
      <PaymentForm>
        <PaymentRow>
          <TextField
            autoComplete="street-address"
            fieldStatus={errorFields['line1'] ? 'error' : 'default'}
            helperText={errorFields['line1']}
            inputClassName="fs-mask"
            label={t(TEXT.BillingAddressLine1Label)}
            name="line1"
            onBlur={handleBlur}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                billingAddress: {
                  ...billingDetails?.billingAddress,
                  line1: e.target.value,
                },
              });
            }}
            value={billingDetails?.billingAddress?.line1}
          />
        </PaymentRow>
        <PaymentRow>
          <TextField
            autoComplete="address-line2"
            inputClassName="fs-mask"
            label={t(TEXT.BillingAddressLine2Label)}
            name="line2"
            onBlur={handleBlur}
            onChange={(e) =>
              setBillingDetails({
                ...billingDetails,
                billingAddress: {
                  ...billingDetails?.billingAddress,
                  line2: e.target.value,
                },
              })
            }
            value={billingDetails?.billingAddress?.line2}
          />
        </PaymentRow>
        <PaymentRow>
          <TextField
            autoComplete="address-level2"
            fieldStatus={errorFields['city'] ? 'error' : 'default'}
            helperText={errorFields['city']}
            inputClassName="fs-mask"
            label={t(TEXT.BillingAddressCityLabel)}
            name="city"
            onBlur={handleBlur}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                billingAddress: {
                  ...billingDetails?.billingAddress,
                  city: e.target.value,
                },
              });
            }}
            value={billingDetails?.billingAddress?.city}
          />
        </PaymentRow>
        <PaymentRow>
          <Dropdown
            autoComplete="address-level1"
            className="fs-mask"
            fieldStatus={errorFields['state'] ? 'error' : 'default'}
            helperText={errorFields['state']}
            label={t(TEXT.BillingAddressStateLabel)}
            name="state"
            onBlur={handleBlur}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                billingAddress: {
                  ...billingDetails?.billingAddress,
                  state: e.target.value,
                },
              });
            }}
            options={STATE_DROPDOWN_OPTIONS}
            value={billingDetails?.billingAddress?.state}
          />
          <TextField
            autoComplete="postal-code"
            fieldStatus={errorFields['postalCode'] ? 'error' : 'default'}
            helperText={errorFields['postalCode']}
            inputClassName="fs-mask"
            label={t(TEXT.BillingAddressPostalLabel)}
            name="postalCode"
            onBlur={handleBlur}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                billingAddress: {
                  ...billingDetails?.billingAddress,
                  postalCode: e.target.value,
                },
              });
            }}
            value={billingDetails?.billingAddress?.postalCode}
          />
        </PaymentRow>
      </PaymentForm>
    </BillingAddressFormContainer>
  );
};
